import { useState, useEffect, useCallback } from 'react';
import { AddItemCard, Modal } from '@/components/ui';
import { EMOJI_COLOR_CLASSES } from '@/components/ui/EmojiPicker/EmojiPicker';
import { ChildListItem } from '@/components/domain/shared/ChildListItem';
import { AddEditChildForm } from '@/pages/onboarding/AddEditChildForm';
import { toast } from '@/stores';
import {
  householdService,
  childService,
  profileImageService,
} from '@/services';
import { usePermissions, useAppShell } from '@/hooks';
import { SettingsSubPage } from './SettingsSubPage';
import { SettingsChildrenSkeleton } from './skeletons';
import type { Child } from '@/types';

export function SettingsChildren() {
  // Children management is a manager capability (Admin + Co-Admin); RLS
  // (children write = my_manager_household_ids) is the real gate.
  const { canEditChildren } = usePermissions();
  const { householdId, householdResolved } = useAppShell();
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);

  // Add / Edit state
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState<Child | null>(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Child | null>(null);

  const loadChildren = useCallback(async (hid: string) => {
    try {
      setLoading(true);
      const data = await householdService.getChildren(hid);
      setChildren(data);
    } catch (err) {
      console.error('Failed to load children:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!householdId) {
      // Only stop showing the skeleton once resolution has FINISHED.
      // Otherwise the first paint (id still pending) renders this page's
      // empty/default state before the real data can possibly arrive.
      if (householdResolved) setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadChildren(householdId);
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId, householdResolved, loadChildren]);

  const handleAdd = async (formData: {
    name: string;
    emoji: string;
    emojiColor: string;
    grade: string;
    school: string;
    imageFile?: File | null;
  }) => {
    if (!householdId) return;
    try {
      const child = await childService.createChild(householdId, {
        name: formData.name,
        emoji_avatar: formData.emoji,
        emoji_color: formData.emojiColor || null,
        grade: formData.grade || null,
        school: formData.school || null,
      });
      let uploadFailed = false;
      if (formData.imageFile) {
        try {
          await profileImageService.uploadAvatarImage(
            'child',
            child.id,
            formData.imageFile,
          );
        } catch (err) {
          console.warn('[SettingsChildren] photo upload failed:', err);
          uploadFailed = true;
        }
      }
      setShowAdd(false);
      const updated = await householdService.getChildren(householdId);
      setChildren(updated);
      if (uploadFailed) {
        toast.error(
          'Child added, but the photo upload failed. Try again from Edit.',
        );
      } else {
        toast.success('Child added');
      }
    } catch (err) {
      console.error('Failed to add child:', err);
      toast.error('Failed to add child');
    }
  };

  const handleEdit = async (formData: {
    name: string;
    emoji: string;
    emojiColor: string;
    grade: string;
    school: string;
  }) => {
    if (!editTarget) return;
    try {
      await childService.updateChild(editTarget.id, {
        name: formData.name,
        emoji_avatar: formData.emoji,
        emoji_color: formData.emojiColor || null,
        grade: formData.grade || null,
        school: formData.school || null,
      });
      setEditTarget(null);
      if (householdId) {
        const updated = await householdService.getChildren(householdId);
        setChildren(updated);
      }
      toast.success('Child updated');
    } catch (err) {
      console.error('Failed to update child:', err);
      toast.error('Failed to update child');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await childService.deleteChild(deleteTarget.id);
      setDeleteTarget(null);
      if (householdId) {
        const updated = await householdService.getChildren(householdId);
        setChildren(updated);
      }
      toast.success('Child removed');
    } catch (err) {
      console.error('Failed to delete child:', err);
      toast.error('Failed to remove child');
      setDeleteTarget(null);
    }
  };

  // --- Add child overlay ---
  if (showAdd) {
    return (
      <AddEditChildForm
        mode="add"
        onSave={handleAdd}
        onBack={() => setShowAdd(false)}
      />
    );
  }

  // --- Edit child overlay ---
  if (editTarget) {
    return (
      <AddEditChildForm
        mode="edit"
        childId={editTarget.id}
        initialData={{
          name: editTarget.name,
          emoji: editTarget.emoji_avatar ?? '',
          emojiColor:
            editTarget.emoji_color ?? (EMOJI_COLOR_CLASSES[0] as string),
          grade: editTarget.grade ?? '',
          school: editTarget.school ?? '',
          imagePath: editTarget.avatar_image_path ?? null,
        }}
        onSave={async (data) => {
          await handleEdit(data);
        }}
        onBack={() => setEditTarget(null)}
      />
    );
  }

  // --- List view ---
  return (
    <SettingsSubPage title="Children">
      {loading ? (
        <SettingsChildrenSkeleton />
      ) : (
        <div className="ph-no-capture flex flex-col gap-3 px-5 py-5">
          {children.length === 0 && (
            <p className="text-sm text-muted-foreground py-4">
              No children added yet.
            </p>
          )}

          {children.map((child) => (
            <ChildListItem
              key={child.id}
              name={child.name}
              emoji={child.emoji_avatar}
              color={child.emoji_color}
              imagePath={child.avatar_image_path}
              grade={child.grade}
              school={child.school}
              onEdit={canEditChildren ? () => setEditTarget(child) : undefined}
              onDelete={
                canEditChildren ? () => setDeleteTarget(child) : undefined
              }
            />
          ))}

          {/* Add child dashed card — managers only. Editors/viewers can
              still see the roster above but can't mutate it.

              Uses the shared `AddItemCard` rather than a local copy. The copy
              here had drifted to a 1px border while the primitive carries the
              2px the team agreed on in review, so the card read as just
              another child row (YAR-230). Sharing it is what stops that
              happening again. */}
          {canEditChildren && (
            <AddItemCard
              label="Add child"
              onClick={() => setShowAdd(true)}
            />
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        bare
      >
        <div className="ph-no-capture flex flex-col gap-4 items-center p-5">
          <p className="text-xl font-medium text-foreground text-center w-full">
            Remove {deleteTarget?.name ?? ''}?
          </p>
          <div className="flex flex-col gap-3 w-full">
            <button
              type="button"
              onClick={handleDelete}
              className="w-full border border-danger text-danger font-semibold text-base leading-6 py-1.5 px-2 rounded-md cursor-pointer"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              className="w-full text-muted-foreground font-semibold text-base leading-6 py-1.5 px-2 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </SettingsSubPage>
  );
}
